// Brand data sources.
//
// GET /api/brands/:brandId/data-connections is served by the backend
// data-sources handler (brandDataSources.dataSources). The public
// fields that handler's ingest check asserts are ingestPath, state,
// directConnected, externalAccountId, and fallback. lastSyncAt is read
// when the payload includes it; the credential query loads connectedAt,
// which is not a sync time and is not shown as one. Account ids and
// tokens are not kept.
//
// A later payload lists status connected, missing, or expiring, plus
// connected, directConnected, state, ingestPath, and lastSyncAt
// (ISO or null), in the order meta, shopify, google ads, ga4, klaviyo,
// instagram. status missing is not connected. status expiring stays
// connected and shows Expiring. A backend Instagram row is kept; the
// status route fills Instagram only when that row is absent.
// Instagram is a credential type the first handler omitted, so a payload
// with no Instagram row is filled from
// GET /api/integrations/instagram/status. That fill does not treat
// connectedAt as a sync time. A 404 still falls back to the
// direct integration status routes (Meta, Instagram, Google Ads,
// Shopify). The card renders only when VITE_DATA_SOURCES=true.

import { apiJson } from '../auth/apiFetch';
import { formatStamp, isMissingEndpoint, safeText } from './client';

/** Provisional view of one source on the brand data card. */
export type DataSourceView = {
  provider: string;
  path: 'Direct' | 'Supermetrics' | 'Not reported' | null;
  health: string;
  lastSync: string | null;
  neededByPlan: boolean;
  problem: string | null;
  /** False hides the ingest-path badge. Unconnected rows are not Direct. */
  connected: boolean;
};

export type DataSourcesResult = {
  sources: DataSourceView[] | null;
  unavailableReason: string | null;
};

type Rec = Record<string, unknown>;

const PROVIDERS: Record<string, string> = {
  meta: 'Meta Ads and catalog',
  'meta ads': 'Meta Ads and catalog',
  'meta ads and catalog': 'Meta Ads and catalog',
  catalog: 'Meta Ads and catalog',
  instagram: 'Instagram',
  shopify: 'Shopify',
  'shopify app': 'Shopify',
  'google ads': 'Google Ads',
  google: 'Google Ads',
  ga4: 'GA4',
  'google analytics': 'GA4',
  klaviyo: 'Klaviyo'
};

export const DATA_CONNECTIONS_MISSING = "Supermetrics, GA4, and Klaviyo aren't in this list yet. They show up when data connections are available. Connected accounts below are the direct integrations already on this brand.";

const DIRECT_STATUSES: { provider: string; url: (brandId: string) => string }[] = [
  { provider: 'Instagram', url: () => '/api/integrations/instagram/status' },
  { provider: 'Meta Ads and catalog', url: () => '/api/integrations/meta-ads/status' },
  { provider: 'Google Ads', url: () => '/api/integrations/google-ads/status' },
  { provider: 'Shopify', url: (brandId) => `/api/integrations/shopify/status?brandId=${encodeURIComponent(brandId)}` }
];

export function dataSourcesEnabled(env: { VITE_DATA_SOURCES?: string } = import.meta.env): boolean {
  return env.VITE_DATA_SOURCES === 'true';
}

export function dataSourcesUrl(brandId: string): string {
  return `/api/brands/${encodeURIComponent(brandId)}/data-connections`;
}

export async function getDataSources(brandId: string): Promise<DataSourcesResult> {
  try {
    const body = await apiJson<unknown>(dataSourcesUrl(brandId), { cache: 'no-store' });
    const sources = readDataSources(body);
    if (!sources) return { sources: null, unavailableReason: "Data sources aren't available yet." };
    return { sources: await withInstagram(sources), unavailableReason: null };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) {
      const direct = await loadDirectIntegrations(brandId);
      if (direct.length) return { sources: direct, unavailableReason: DATA_CONNECTIONS_MISSING };
    }
    return { sources: null, unavailableReason: "Data sources aren't available yet." };
  }
}

export function readDataSources(body: unknown): DataSourceView[] | null {
  const found = sourceRows(body);
  if (!found) return null;
  const sources = found.rows.map(readSource).filter((row): row is DataSourceView => row != null);
  if (Array.isArray(body)) return sources.length ? sources : null;
  return sources;
}

/** One direct integration status payload, from the existing Integrations card routes. */
export function directSourceFromStatus(provider: string, body: unknown): DataSourceView | null {
  if (!isRec(body)) return null;
  const single = isRec(body.credential) ? body.credential : null;
  const many = Array.isArray(body.credentials) ? body.credentials.filter(isRec) : [];
  const cred = single ?? many.find((row) => row.status === 'active' || row.status === 'pending') ?? null;
  if (!cred) return null;
  const status = typeof cred.status === 'string' ? cred.status : '';
  if (status !== 'active' && status !== 'pending') return null;
  return {
    provider,
    path: 'Direct',
    health: status === 'active' ? 'Healthy' : 'Warning',
    lastSync: formatStamp(cred.lastUsedAt ?? cred.lastCatalogSyncAt),
    neededByPlan: false,
    problem: null,
    connected: true
  };
}

async function loadDirectIntegrations(brandId: string): Promise<DataSourceView[]> {
  const rows = await Promise.all(DIRECT_STATUSES.map(async (item) => {
    try {
      const body = await apiJson<unknown>(item.url(brandId), { cache: 'no-store' });
      return directSourceFromStatus(item.provider, body);
    } catch {
      return null;
    }
  }));
  return rows.filter((row): row is DataSourceView => row != null);
}

const CONNECTED_WORDS = new Set([
  'connected', 'healthy', 'ok', 'active', 'warning', 'degraded', 'error', 'failed', 'expiring'
]);
const NOT_CONNECTED_WORDS = new Set([
  'not connected', 'not configured', 'missing', 'disconnected', 'unconfigured', 'absent', 'off', 'none'
]);

function readSource(row: Rec): DataSourceView | null {
  const provider = providerLabel(row.provider ?? row.source ?? row.id ?? row.name ?? row.type);
  if (!provider) return null;
  const connected = connectionOf(row);
  const rawPath = row.ingestPath ?? row.path;
  const labeled = rawPath == null || rawPath === '' ? null : pathLabel(rawPath);
  const error = problemText(row.lastError ?? row.error);
  const problem = error ? error : (row.lastError || row.error ? 'The last sync failed.' : null);
  const neededByPlan = row.neededByPlan === true || row.needed === true;
  const lastSync = formatStamp(row.lastSyncAt ?? row.lastSync ?? row.syncedAt ?? row.lastIngestAt);

  if (connected === false) {
    return {
      provider,
      path: null,
      health: 'Not connected',
      lastSync: null,
      neededByPlan,
      problem,
      connected: false
    };
  }

  if (connected === null) {
    const path = labeled ?? (rawPath == null || rawPath === '' ? 'Not reported' : null);
    if (!path) return null;
    return {
      provider,
      path,
      health: healthLabel(row.health ?? row.status) ?? 'Not reported',
      lastSync,
      neededByPlan,
      problem,
      connected: true
    };
  }

  return {
    provider,
    path: labeled ?? 'Not reported',
    health: sourceHealth(row) ?? 'Connected',
    lastSync,
    neededByPlan,
    problem,
    connected: true
  };
}

function connectionOf(row: Rec): boolean | null {
  const state = word(row.state);
  const status = word(row.status);
  const health = word(row.health);
  const path = pathLabel(row.ingestPath ?? row.path);
  if (state && NOT_CONNECTED_WORDS.has(state)) return false;
  if (status && NOT_CONNECTED_WORDS.has(status)) return false;
  if (state === 'expiring' || status === 'expiring') return true;
  if (row.directConnected === true || row.connected === true) return true;
  if (row.fallback === true && path === 'Supermetrics') return true;
  if (state && CONNECTED_WORDS.has(state)) return true;
  if (row.directConnected === false || row.connected === false) return false;
  if (health && CONNECTED_WORDS.has(health)) return true;
  if (status && CONNECTED_WORDS.has(status)) return true;
  return null;
}

async function withInstagram(sources: DataSourceView[]): Promise<DataSourceView[]> {
  if (sources.some((row) => row.provider === 'Instagram')) return sources;
  try {
    const body = await apiJson<unknown>('/api/integrations/instagram/status', { cache: 'no-store' });
    const row = instagramFill(body);
    return row ? [...sources, row] : sources;
  } catch {
    return sources;
  }
}

function instagramFill(body: unknown): DataSourceView | null {
  if (!isRec(body)) return null;
  const live = directSourceFromStatus('Instagram', body);
  if (live) return live.health === 'Healthy' ? { ...live, health: 'Connected' } : live;
  if (body.credential === null) return disconnectedInstagram();
  if (Array.isArray(body.credentials) && body.credentials.length === 0 && body.credential == null) {
    return disconnectedInstagram();
  }
  return null;
}

function disconnectedInstagram(): DataSourceView {
  return {
    provider: 'Instagram',
    path: null,
    health: 'Not connected',
    lastSync: null,
    neededByPlan: false,
    problem: null,
    connected: false
  };
}

function sourceRows(body: unknown): { rows: Rec[] } | null {
  if (Array.isArray(body)) return { rows: records(body) };
  if (!isRec(body)) return null;
  const containers = [body];
  if (isRec(body.status)) containers.push(body.status);
  if (isRec(body.data)) containers.push(body.data);
  for (const payload of containers) {
    for (const key of ['sources', 'connections', 'dataConnections', 'items'] as const) {
      if (!(key in payload)) continue;
      return { rows: asRecords(payload[key]) };
    }
  }
  return null;
}

function asRecords(value: unknown): Rec[] {
  if (Array.isArray(value)) return records(value);
  if (!isRec(value)) return [];
  return Object.entries(value).flatMap(([key, row]) => (isRec(row) ? [{ id: key, ...row }] : []));
}

function word(value: unknown): string | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  return raw.toLowerCase().replace(/[_-]+/g, ' ');
}

function providerLabel(value: unknown): string | null {
  const raw = prose(value, 60);
  if (!raw) return null;
  return PROVIDERS[raw.toLowerCase().replace(/[_-]+/g, ' ')] ?? raw;
}

function pathLabel(value: unknown): DataSourceView['path'] | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'direct') return 'Direct';
  if (key === 'supermetrics' || key === 'super metrics') return 'Supermetrics';
  return null;
}

function sourceHealth(row: Rec): string | null {
  if (row.health != null && row.health !== '') {
    const fromHealth = healthLabel(row.health);
    if (fromHealth) return fromHealth;
  }
  const status = word(row.status);
  if (status === 'connected' || status === 'missing') return null;
  return healthLabel(row.status);
}

function healthLabel(value: unknown): string | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  const labels: Record<string, string> = {
    healthy: 'Healthy',
    ok: 'Healthy',
    connected: 'Healthy',
    warning: 'Warning',
    degraded: 'Warning',
    error: 'Error',
    failed: 'Error',
    missing: 'Missing',
    disconnected: 'Missing',
    expiring: 'Expiring'
  };
  return labels[key] ?? null;
}

function problemText(value: unknown): string | null {
  if (typeof value !== 'string') return prose(value, 200);
  const stripped = value.replace(/\b[a-f0-9]{24}\b/gi, ' ').replace(/\s+/g, ' ').trim();
  return prose(stripped, 200);
}

function prose(value: unknown, limit: number): string | null {
  const text = safeText(value, limit);
  if (!text || looksLikePii(text)) return null;
  return text;
}

function looksLikePii(text: string): boolean {
  if (/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return true;
  if (/\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/.test(text)) return true;
  return false;
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}
